import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import {
  consecutiveDaysFrom,
  formatArrivalDayChip,
  formatArrivalDayLong,
  todayLocal,
} from "@/lib/bookingDates";
import { canWriteGuestPitchBooking } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { DeparturesTable } from "@/components/bookings/DeparturesTable";
import { useGuestById } from "@/lib/useGuestById";
import type { BookingDeparture, DepartureDayCount, Pitch } from "@/types/camping";
import { EditBookingModal } from "@/pages/bookings/EditBookingModal";
import { DepartureCheckoutModal } from "@/components/bookings/DepartureCheckoutModal";
import { GuestStayModal } from "@/components/bookings/GuestStayModal";
import { confirmRiskyCheckOut } from "@/lib/checkoutConfirm";
import { hasOpenInvoiceAmount } from "@/lib/bookingInvoiceDisplay";
import { OpenedInvoiceDraftModal } from "@/components/invoices/OpenedInvoiceDraftModal";
import type { DepartureCheckoutModalVariant } from "@/components/bookings/DepartureCheckoutModal";

const DEPARTURE_DAY_COUNT = 7;

function formatMoney(amount: string, currency: string): string {
  const n = Number(amount);
  if (Number.isNaN(n)) return `${amount} ${currency}`;
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency || "EUR",
  }).format(n);
}

export function DeparturesPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteGuestPitchBooking(groups);
  const today = todayLocal();
  const dayOptions = useMemo(() => consecutiveDaysFrom(today, DEPARTURE_DAY_COUNT), [today]);

  const [selectedDate, setSelectedDate] = useState(today);
  const [bookings, setBookings] = useState<BookingDeparture[]>([]);
  const [checkoutStrict, setCheckoutStrict] = useState(false);
  const [departureTime, setDepartureTime] = useState("11:00");
  const [departureModalOpen, setDepartureModalOpen] = useState(false);
  const [departureModalVariant, setDepartureModalVariant] =
    useState<DepartureCheckoutModalVariant>("key-capture");
  const [departureModalBooking, setDepartureModalBooking] =
    useState<BookingDeparture | null>(null);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkOutLoadingId, setCheckOutLoadingId] = useState<string | null>(null);
  const [stayModalBooking, setStayModalBooking] = useState<BookingDeparture | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<BookingDeparture | null>(null);
  const [departureCounts, setDepartureCounts] = useState<Record<string, number>>({});
  const [openedInvoiceId, setOpenedInvoiceId] = useState<string | null>(null);

  const isToday = selectedDate === today;
  const canCheckOutToday = isToday;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<BookingDeparture[]>(
        `/api/v1/bookings/departures?departure_date=${selectedDate}`,
      ),
      api.get<Pitch[]>("/api/v1/pitches"),
      api.get<{ checkout: { requires_paid_invoice: boolean; departure_time?: string } }>(
        "/api/v1/settings",
      ),
    ])
      .then(([b, p, settings]) => {
        setBookings(b);
        setPitches(p);
        setCheckoutStrict(settings.checkout?.requires_paid_invoice ?? false);
        setDepartureTime(settings.checkout?.departure_time ?? "11:00");
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedDate]);

  useEffect(() => {
    if (!dayOptions.includes(selectedDate)) {
      setSelectedDate(today);
    }
  }, [dayOptions, selectedDate, today]);

  const loadCounts = useCallback(() => {
    api
      .get<DepartureDayCount[]>(
        `/api/v1/bookings/departures/summary?from=${today}&days=${DEPARTURE_DAY_COUNT}`,
      )
      .then((rows) => {
        const map: Record<string, number> = {};
        for (const row of rows) {
          map[row.departure_date] = row.count;
        }
        setDepartureCounts(map);
      })
      .catch(() => {
        /* Zähler optional */
      });
  }, [today]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    const title = isToday ? "Abreisen heute" : "Abreisen";
    const subtitle = isToday
      ? `Ausstehende Abreisen am ${formatArrivalDayLong(today)}`
      : `Ausstehende Abreisen am ${formatArrivalDayLong(selectedDate)}`;
    setPageMeta({ title, subtitle });
    load();
    return () => setPageMeta(null);
  }, [isToday, load, selectedDate, setPageMeta, today]);

  const stayModalGuest = useGuestById(stayModalBooking?.guest_id);

  const stayModalPitch = useMemo(
    () =>
      stayModalBooking?.pitch_id
        ? pitches.find((p) => p.id === stayModalBooking.pitch_id) ?? null
        : null,
    [stayModalBooking, pitches],
  );

  const selectedBookingGuest = useGuestById(selectedBooking?.guest_id);

  const introText = isToday
    ? `Eingecheckt, Abreise ab ${departureTime} Uhr. Eine Zeile öffnet Buchung und Rechnungen.`
    : "Eingecheckte Gäste mit geplanter Abreise an diesem Tag.";

  const emptyMessage = isToday
    ? "Keine ausstehenden Abreisen für heute."
    : `Keine ausstehenden Abreisen am ${formatArrivalDayLong(selectedDate)}.`;

  function hasOpenInvoices(b: BookingDeparture): boolean {
    return hasOpenInvoiceAmount(b);
  }

  function checkoutBlocked(b: BookingDeparture): boolean {
    return checkoutStrict && hasOpenInvoices(b);
  }

  function openDepartureModal(
    variant: DepartureCheckoutModalVariant,
    booking?: BookingDeparture,
  ) {
    setDepartureModalVariant(variant);
    setDepartureModalBooking(booking ?? null);
    setDepartureModalOpen(true);
  }

  async function handleCheckOut(booking: BookingDeparture) {
    if (!canEdit || !canCheckOutToday || booking.status !== "checked_in") return;
    if (checkoutBlocked(booking)) return;

    if (!checkoutStrict && !confirmRiskyCheckOut(booking, formatMoney)) return;

    setCheckOutLoadingId(booking.id);
    setError(null);
    try {
      const updated = await api.post<BookingDeparture>(
        `/api/v1/bookings/${booking.id}/check-out`,
        {},
      );
      load();
      loadCounts();
      setStayModalBooking(null);
      setDepartureModalOpen(false);
      setDepartureModalBooking(null);
      if (updated.opened_invoice_id) {
        setOpenedInvoiceId(updated.opened_invoice_id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Check-out fehlgeschlagen.");
    } finally {
      setCheckOutLoadingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3" data-tour="departures-day-picker">
        <p className="text-sm font-medium text-label">Tag wählen</p>
        <div className="overflow-x-auto pb-1">
          <div
            className="eh-segmented eh-segmented--md min-w-max"
            role="tablist"
            aria-label="Abreisetag wählen"
          >
            {dayOptions.map((day) => {
              const count = departureCounts[day] ?? 0;
              return (
                <button
                  key={day}
                  type="button"
                  role="tab"
                  aria-selected={day === selectedDate}
                  className={clsx(
                    "eh-segmented-item flex flex-col items-center gap-0.5 py-2",
                    day === selectedDate && "is-active",
                  )}
                  onClick={() => setSelectedDate(day)}
                >
                  <span>{formatArrivalDayChip(day, today)}</span>
                  <span
                    className={clsx("eh-stay-day-count", count > 0 && "is-hot")}
                    aria-label={`${count} Abreisen`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <p className="text-sm text-label-secondary">{introText}</p>

      <div className="flex flex-wrap items-end gap-3">
        <Button
          type="button"
          variant="secondary"
          onClick={() => openDepartureModal("key-capture")}
        >
          Schlüssel & Transponder erfassen
        </Button>
      </div>

      {checkoutStrict ? (
        <p className="text-sm text-status-amber">
          Check-out ist blockiert, solange ausgestellte Rechnungen offen sind (Einstellung aktiv).
        </p>
      ) : (
        <p className="text-sm text-label-tertiary">
          Bei offenen Rechnungen oder fehlender Rechnung ist eine doppelte Bestätigung nötig – Check-out
          ist dennoch möglich.
        </p>
      )}

      <OpenedInvoiceDraftModal
        invoiceId={openedInvoiceId}
        open={!!openedInvoiceId}
        userGroups={groups}
        onClose={() => setOpenedInvoiceId(null)}
        onChanged={() => load()}
      />

      <DepartureCheckoutModal
        open={departureModalOpen}
        variant={departureModalVariant}
        onClose={() => {
          setDepartureModalOpen(false);
          setDepartureModalBooking(null);
        }}
        pitches={pitches}
        referenceDate={selectedDate}
        departureTime={departureTime}
        canEdit={canEdit}
        checkoutStrict={checkoutStrict}
        userGroups={groups}
        checkOutLoadingId={checkOutLoadingId}
        initialBooking={departureModalBooking}
        onCheckOut={handleCheckOut}
        onChanged={() => {
          load();
          loadCounts();
        }}
        onInvoiceDraftOpened={setOpenedInvoiceId}
      />

      <GuestStayModal
        open={!!stayModalBooking}
        booking={stayModalBooking}
        guest={stayModalGuest}
        pitch={stayModalPitch}
        canEdit={canEdit}
        userGroups={groups}
        onInvoiceChanged={() => {
          load();
          loadCounts();
        }}
        checkoutStrict={checkoutStrict}
        checkOutLoading={checkOutLoadingId === stayModalBooking?.id}
        onClose={() => setStayModalBooking(null)}
        onCheckOut={
          stayModalBooking?.status === "checked_in" && canCheckOutToday
            ? () => {
                openDepartureModal("guest-checkout", stayModalBooking);
                setStayModalBooking(null);
              }
            : undefined
        }
        onEditBooking={() => {
          if (stayModalBooking) {
            setSelectedBooking(stayModalBooking);
            setStayModalBooking(null);
          }
        }}
        onOpenInvoice={() => {
          if (stayModalBooking) {
            setSelectedBooking(stayModalBooking);
            setStayModalBooking(null);
          }
        }}
      />

      <EditBookingModal
        open={!!selectedBooking}
        booking={selectedBooking}
        guest={selectedBookingGuest}
        pitches={pitches}
        onClose={() => setSelectedBooking(null)}
        onSaved={() => {
          load();
          loadCounts();
        }}
        canEdit={canEdit}
      />

      {error && <p className="text-sm text-status-red">{error}</p>}
      {loading ? (
        <p className="text-sm text-label-secondary">Lade Abreisen…</p>
      ) : (
        <div data-tour="departures-table">
          <DeparturesTable
            bookings={bookings}
            pitches={pitches}
            canEdit={canEdit}
            canCheckOutToday={canCheckOutToday}
            checkoutStrict={checkoutStrict}
            checkOutLoadingId={checkOutLoadingId}
            listKey={selectedDate}
            emptyMessage={emptyMessage}
            onOpen={setStayModalBooking}
            onCheckOut={(booking) => openDepartureModal("guest-checkout", booking)}
          />
        </div>
      )}
    </div>
  );
}
